// Immutable, invariant-preserving edit helpers for the editor. The server re-validates every save; these helpers
// keep the local document valid by construction (sorted keyframes, clamped ranges, element<->scene integrity).
import { evaluate } from "../../../engine/src/evaluate.ts";
import type { AnimProperty, CameraElement, Easing, Element, MeshElement, ParticlesElement, Project, Scene, ShapeElement, TextElement,
  Track, Transform } from "../../../engine/src/types.ts";

export const MAX_DURATION_MS = 300_000;
export const RANGES: Record<AnimProperty, [number, number]> = {
  x: [-20000, 20000], y: [-20000, 20000], z: [-20000, 20000], scale_x: [0, 100], scale_y: [0, 100], scale_z: [0, 100],
  rotation: [-36000, 36000], rotation_x: [-36000, 36000], rotation_y: [-36000, 36000], opacity: [0, 1],
  distance: [0.05, 10], fov: [5, 120], blur: [0, 100], reveal: [0, 1], draw: [0, 1], morph: [0, 1],
};
const CAMERA_PROPS = new Set<AnimProperty>(["x", "y", "z", "rotation", "rotation_x", "rotation_y", "distance", "fov"]);
const OBJECT_PROPS = new Set<AnimProperty>(["x", "y", "z", "scale_x", "scale_y", "scale_z", "rotation", "rotation_x", "rotation_y", "opacity"]);
const SOFT_PROPS = new Set<AnimProperty>([...OBJECT_PROPS, "blur", "reveal"]);          // text, shapes and particles
export function animatable(el: Element): AnimProperty[] {
  if (el.type === "code") return [];            // the code owns all motion
  if (el.type === "shape") return [...SOFT_PROPS, "draw", "morph"];
  return [...(el.type === "camera" ? CAMERA_PROPS : el.type === "mesh" ? OBJECT_PROPS : SOFT_PROPS)];
}
export const clampProp = (p: AnimProperty, v: number): number => {
  const [lo, hi] = RANGES[p];
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : lo;
};

let counter = 0;
export function uid(prefix: string): string {
  counter = (counter + 1) % 1_000_000;
  const rand = Math.floor(Math.random() * 0xffffff).toString(36);
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${rand}`.slice(0, 40);
}

export const DEFAULT_TRANSFORM: Transform = { x: 0, y: 0, z: 0, scale_x: 1, scale_y: 1, scale_z: 1, rotation: 0,
  rotation_x: 0, rotation_y: 0, opacity: 1 };

export type NewKind = "text" | "rect" | "ellipse" | "star" | "ring" | "particles" | "cube" | "sphere" | "torus" | "cylinder" | "cone" | "camera";
export function newElement(kind: NewKind, p: Project): Element {
  const base = { id: uid("el"), name: "", visible: true, transform: { ...DEFAULT_TRANSFORM, x: p.width / 2, y: p.height / 2 } };
  const s = Math.round(Math.min(p.width, p.height) * 0.25);
  switch (kind) {
    case "text": return { ...base, type: "text", text: "Text", font_family: "sans", font_size: Math.round(p.height * 0.06),
      font_weight: 700, fill: "#18181B", align: "center", max_width: Math.round(p.width * 0.8) } satisfies TextElement;
    case "rect": case "ellipse": case "star": case "ring": return { ...base, type: "shape", shape: kind, width: s, height: s, fill: "#F04452",
      corner_radius: 0 } satisfies ShapeElement;
    case "particles": return { ...base, type: "particles", kind: "bokeh", count: 40, color: "#FFFFFF", color_to: "#FF9CC2", size: 40, size_variation: 0.5,
      area_w: p.width, area_h: p.height, speed: 30, gravity: 0, drift: 30, lifetime_ms: 6000, seed: 1, glow: 0.9, additive: true } satisfies ParticlesElement;
    case "camera": return { ...base, type: "camera", fov: 35, distance: 1, transform: { ...DEFAULT_TRANSFORM } } satisfies CameraElement;
    default: return { ...base, type: "mesh", mesh: kind, size: s, color: "#F04452", metalness: 0.3, roughness: 0.4,
      emissive: "#000000", emissive_intensity: 0 } satisfies MeshElement;
  }
}

/** A picture from the library, centred and sized to fit the canvas. */
export function newImageElement(p: Project, asset: { id: string; name: string; width?: number; height?: number }): Element {
  const iw = Math.max(1, asset.width ?? 400), ih = Math.max(1, asset.height ?? 400);
  const fit = Math.min(4, (p.width * 0.7) / iw, (p.height * 0.5) / ih);     // fills at most 70% x 50% of the canvas; small icons grow up to 4x
  return { id: uid("el"), name: asset.name.slice(0, 40), visible: true, transform: { ...DEFAULT_TRANSFORM, x: p.width / 2, y: p.height / 2 },
    type: "image", asset_id: asset.id, width: Math.round(iw * fit), height: Math.round(ih * fit), fit: "contain", corner_radius: 0,
    reveal_effect: "scale", reveal_distance: 40 } as Element;
}

/** Place a video as a full-frame scene layer; its embedded sound is stored as a normal synchronized audio clip. */
export function newVideoElement(p: Project, asset: { id: string; name: string; width?: number; height?: number; duration_ms?: number }): Element {
  const iw = Math.max(16, asset.width ?? p.width), ih = Math.max(16, asset.height ?? p.height);
  const scale = Math.max(p.width / iw, p.height / ih);
  const duration = Math.max(100, Math.min(asset.duration_ms ?? 3000, MAX_DURATION_MS));
  return { id: uid("el"), name: asset.name.slice(0, 40), visible: true, transform: { ...DEFAULT_TRANSFORM, x: p.width / 2, y: p.height / 2 },
    type: "video", asset_id: asset.id, width: p.width, height: p.height, fit: "cover", corner_radius: 0,
    start_ms: 0, duration_ms: duration, in_ms: 0, speed: 1 };
}

export function sceneOf(p: Project, elementId: string): Scene | undefined {
  return p.scenes.find((s) => s.element_ids.includes(elementId));
}
export function sceneAt(p: Project, timeMs: number): Scene | undefined {
  const sorted = [...p.scenes].sort((a, b) => a.start_ms - b.start_ms);
  return sorted.find((s) => timeMs >= s.start_ms && timeMs < s.start_ms + s.duration_ms) ?? sorted.find((s) => timeMs === s.start_ms + s.duration_ms);
}
export function nextSceneStart(p: Project): number {
  return p.scenes.reduce((m, s) => Math.max(m, s.start_ms + s.duration_ms), 0);
}

export function addScene(p: Project, durationMs = 3000): { project: Project; sceneId: string } {
  const start = nextSceneStart(p), dur = Math.min(durationMs, MAX_DURATION_MS - start);
  if (dur < 100) return { project: p, sceneId: "" };
  const id = uid("sc");
  const scene: Scene = { id, name: "", start_ms: start, duration_ms: dur, background: "#FFFFFF", background_gradient: null,
    lighting: { ambient: 0.55, key_intensity: 1.2, key_color: "#FFFFFF", key_yaw: -35, key_pitch: 40, sky: "#DDE6FF", ground: "#3A3A44" }, element_ids: [] };
  return { project: { ...p, scenes: [...p.scenes, scene], duration_ms: Math.max(p.duration_ms, start + dur) }, sceneId: id };
}

export function updateScene(p: Project, id: string, patch: Partial<Omit<Scene, "id" | "element_ids">>): Project {
  const sc = p.scenes.find((s) => s.id === id);
  if (!sc) return p;
  const start = Math.max(0, Math.round(patch.start_ms ?? sc.start_ms));
  let dur = Math.max(100, Math.round(patch.duration_ms ?? sc.duration_ms));
  dur = Math.min(dur, MAX_DURATION_MS - start);
  // keyframes may never fall after the end of the scene
  const lastKf = p.tracks.filter((t) => sc.element_ids.includes(t.element_id))
    .reduce((m, t) => Math.max(m, ...t.keyframes.map((k) => k.time_ms)), 0);
  dur = Math.max(dur, lastKf);
  const next: Scene = { ...sc, ...patch, start_ms: start, duration_ms: dur };
  const scenes = p.scenes.map((s) => (s.id === id ? next : s));
  return { ...p, scenes, duration_ms: Math.max(p.duration_ms, scenes.reduce((m, s) => Math.max(m, s.start_ms + s.duration_ms), 0)) };
}

export function removeScene(p: Project, id: string): Project {
  const sc = p.scenes.find((s) => s.id === id);
  if (!sc) return p;
  const gone = new Set(sc.element_ids);
  const elements = Object.fromEntries(Object.entries(p.elements).filter(([k]) => !gone.has(k)));
  return { ...p, scenes: p.scenes.filter((s) => s.id !== id), elements, tracks: p.tracks.filter((t) => !gone.has(t.element_id)) };
}

export function addElement(p: Project, sceneId: string, el: Element): Project {
  const sc = p.scenes.find((s) => s.id === sceneId);
  if (!sc || p.elements[el.id]) return p;
  if (el.type === "camera" && sc.element_ids.some((id) => p.elements[id]?.type === "camera")) return p;  // one camera per scene
  if (Object.keys(p.elements).length >= 600) return p;
  return { ...p, elements: { ...p.elements, [el.id]: el },
    scenes: p.scenes.map((s) => (s.id === sceneId ? { ...s, element_ids: [...s.element_ids, el.id] } : s)) };
}

/** Put an expanded component into a scene: its elements join the scene (z-order top), its keyframe tracks join the project.
 *  Refuses (returns null) when it would exceed the element limit or run past the end of the scene, rather than clipping it. */
export function addComponentInstance(p: Project, sceneId: string, ex: { elements: Element[]; tracks: Track[] }, dx: number, dy: number): { project: Project; ids: string[] } | { error: "too_many" | "too_long" | "exists" | "no_scene"; needed_ms?: number } {
  const sc = p.scenes.find((s) => s.id === sceneId);
  if (!sc) return { error: "no_scene" };
  if (Object.keys(p.elements).length + ex.elements.length > 600) return { error: "too_many" };
  if (ex.elements.some((e) => p.elements[e.id])) return { error: "exists" };
  const end = Math.max(0, ...ex.tracks.flatMap((t) => t.keyframes.map((k) => k.time_ms)));
  if (end >= sc.duration_ms) return { error: "too_long", needed_ms: end + 1 };
  const elements = { ...p.elements };
  const base = Math.max(0, ...sc.element_ids.map((id) => p.elements[id]?.layer ?? 0));      // flat elements are drawn by `layer`: stay above what is there
  for (const e of ex.elements) elements[e.id] = { ...e, layer: Math.max(-100, Math.min(100, (e.layer ?? 0) + base)), transform: { ...e.transform, x: e.transform.x + dx, y: e.transform.y + dy } } as Element;
  const tracks = ex.tracks.map((t) => (t.property === "x" || t.property === "y"
    ? { ...t, keyframes: t.keyframes.map((k) => ({ ...k, value: k.value + (t.property === "x" ? dx : dy) })) } : t));
  const ids = ex.elements.map((e) => e.id);
  return { project: { ...p, elements, tracks: [...p.tracks, ...tracks],
    scenes: p.scenes.map((s) => (s.id === sceneId ? { ...s, element_ids: [...s.element_ids, ...ids] } : s)) }, ids };
}

export function removeElement(p: Project, id: string): Project {
  if (!p.elements[id]) return p;
  const { [id]: _gone, ...rest } = p.elements;
  return { ...p, elements: rest, tracks: p.tracks.filter((t) => t.element_id !== id),
    scenes: p.scenes.map((s) => ({ ...s, element_ids: s.element_ids.filter((e) => e !== id) })) };
}

/** Change non-animatable fields (text, colors, fonts, shape size, ...). Type and id are immutable. */
export function updateElement(p: Project, id: string, patch: Record<string, unknown>): Project {
  const el = p.elements[id];
  if (!el) return p;
  const { id: _i, type: _t, transform: _tr, ...safe } = patch as Record<string, unknown>;
  return { ...p, elements: { ...p.elements, [id]: { ...el, ...safe } as Element } };
}

export function trackOf(p: Project, id: string, prop: AnimProperty): Track | undefined {
  return p.tracks.find((t) => t.element_id === id && t.property === prop);
}

export function resolvedValue(p: Project, id: string, prop: AnimProperty, timeMs: number): number {
  const sc = sceneOf(p, id), el = p.elements[id];
  if (!sc || !el) return 0;
  const base = prop === "fov" ? (el as CameraElement).fov : prop === "distance" ? (el as CameraElement).distance
    : (el.transform as unknown as Record<string, number>)[prop] ?? (prop === "reveal" || prop === "draw" ? 1 : 0);
  const tr = trackOf(p, id, prop);
  if (!tr) return base;
  const t = Math.min(Math.max(timeMs, sc.start_ms), sc.start_ms + sc.duration_ms);
  const f = evaluate(p, t);
  const st = f.scenes.find((s) => s.scene.id === sc.id);
  if (!st) return base;
  if (el.type === "camera") {
    const c = st.camera;
    return ({ x: c.x, y: c.y, z: c.z, rotation: c.roll, rotation_x: c.pitch, rotation_y: c.yaw, distance: c.distance, fov: c.fov } as Record<string, number>)[prop] ?? base;
  }
  return (st.objects.find((o) => o.id === id)?.t as unknown as Record<string, number> | undefined)?.[prop] ?? base;
}

function setBase(p: Project, id: string, prop: AnimProperty, value: number): Project {
  const el = p.elements[id];
  if (!el) return p;
  const v = clampProp(prop, value);
  if (el.type === "camera" && (prop === "fov" || prop === "distance")) return { ...p, elements: { ...p.elements, [id]: { ...el, [prop]: v } } };
  if (prop === "fov" || prop === "distance") return p;
  return { ...p, elements: { ...p.elements, [id]: { ...el, transform: { ...el.transform, [prop]: v } } as Element } };
}

export function setKeyframe(p: Project, id: string, prop: AnimProperty, timeMs: number, value: number, easing: Easing = "ease_in_out"): Project {
  const sc = sceneOf(p, id), el = p.elements[id];
  if (!sc || !el || !animatable(el).includes(prop)) return p;
  const local = Math.min(Math.max(0, Math.round(timeMs - sc.start_ms)), sc.duration_ms);
  const v = clampProp(prop, value);
  const existing = trackOf(p, id, prop);
  if (!existing) {
    const tr: Track = { id: uid("trk"), element_id: id, property: prop, keyframes: [{ id: uid("kf"), time_ms: local, value: v, easing }] };
    return { ...p, tracks: [...p.tracks, tr] };
  }
  const hit = existing.keyframes.find((k) => k.time_ms === local);
  const kfs = hit ? existing.keyframes.map((k) => (k === hit ? { ...k, value: v } : k))
    : [...existing.keyframes, { id: uid("kf"), time_ms: local, value: v, easing }].sort((a, b) => a.time_ms - b.time_ms);
  if (kfs.length > 64) return p;
  return { ...p, tracks: p.tracks.map((t) => (t === existing ? { ...t, keyframes: kfs } : t)) };
}

export function removeKeyframe(p: Project, trackId: string, kfId: string): Project {
  const tr = p.tracks.find((t) => t.id === trackId);
  if (!tr) return p;
  const kfs = tr.keyframes.filter((k) => k.id !== kfId);
  return { ...p, tracks: kfs.length ? p.tracks.map((t) => (t === tr ? { ...t, keyframes: kfs } : t)) : p.tracks.filter((t) => t !== tr) };
}

/** Move a keyframe in scene-local time; keeps strict ordering by clamping between its neighbours. */
export function moveKeyframe(p: Project, trackId: string, kfId: string, localMs: number): Project {
  const tr = p.tracks.find((t) => t.id === trackId);
  if (!tr) return p;
  const sc = sceneOf(p, tr.element_id);
  const i = tr.keyframes.findIndex((k) => k.id === kfId);
  if (!sc || i < 0) return p;
  const lo = i > 0 ? tr.keyframes[i - 1]!.time_ms + 1 : 0;
  const hi = i < tr.keyframes.length - 1 ? tr.keyframes[i + 1]!.time_ms - 1 : sc.duration_ms;
  const t = Math.min(hi, Math.max(lo, Math.round(localMs)));
  if (t === tr.keyframes[i]!.time_ms) return p;
  return { ...p, tracks: p.tracks.map((x) => (x === tr ? { ...x, keyframes: x.keyframes.map((k) => (k.id === kfId ? { ...k, time_ms: t } : k)) } : x)) };
}

export function setKeyframeEasing(p: Project, trackId: string, kfId: string, easing: Easing): Project {
  return { ...p, tracks: p.tracks.map((t) => (t.id !== trackId ? t : { ...t, keyframes: t.keyframes.map((k) => (k.id === kfId ? { ...k, easing } : k)) })) };
}

/** What every inspector field and canvas drag calls. If the property is animated, editing at the playhead writes a
 *  keyframe (so the edit is not lost on playback); otherwise it changes the base value. */
export function setPropertyAtTime(p: Project, id: string, prop: AnimProperty, value: number, timeMs: number): Project {
  const sc = sceneOf(p, id);
  if (!sc) return p;
  return trackOf(p, id, prop) ? setKeyframe(p, id, prop, timeMs, value) : setBase(p, id, prop, value);
}

/** Diamond button: add a keyframe holding the current value, or remove the one under the playhead. */
export function toggleKeyframe(p: Project, id: string, prop: AnimProperty, timeMs: number): Project {
  const sc = sceneOf(p, id), tr = trackOf(p, id, prop);
  if (!sc) return p;
  const local = Math.round(timeMs - sc.start_ms);
  const hit = tr?.keyframes.find((k) => k.time_ms === local);
  if (tr && hit) return removeKeyframe(p, tr.id, hit.id);
  return setKeyframe(p, id, prop, timeMs, resolvedValue(p, id, prop, timeMs));
}

export function duplicateElement(p: Project, id: string): { project: Project; id: string } {
  const el = p.elements[id], sc = sceneOf(p, id);
  if (!el || !sc || el.type === "camera") return { project: p, id };
  const nid = uid("el");
  let next = addElement(p, sc.id, { ...structuredClone(el), id: nid, name: el.name ? el.name + " copy" : "",
    transform: { ...el.transform, x: el.transform.x + 40, y: el.transform.y + 40 } } as Element);
  for (const t of p.tracks.filter((t) => t.element_id === id)) {
    next = { ...next, tracks: [...next.tracks, { ...structuredClone(t), id: uid("trk"), element_id: nid,
      keyframes: t.keyframes.map((k) => ({ ...k, id: uid("kf") })) }] };
  }
  return { project: next, id: nid };
}

/** Split a video at project time into two contiguous clips while keeping source time and animation continuous. */
export function splitVideoElement(p: Project, id: string, timeMs: number): { project: Project; id: string } {
  const el = p.elements[id], sc = sceneOf(p, id);
  if (!el || el.type !== "video" || !sc) return { project: p, id: "" };
  const local = Math.round(timeMs - sc.start_ms), cut = local - el.start_ms;
  if (cut < 100 || cut > el.duration_ms - 100) return { project: p, id: "" };
  const copy = duplicateElement(p, id);
  let next = updateElement(copy.project, id, { duration_ms: cut });
  next = updateElement(next, copy.id, { name: el.name ? `${el.name} (2)` : "Video (2)", start_ms: local,
    duration_ms: el.duration_ms - cut, in_ms: el.in_ms + Math.round(cut * el.speed) });
  const current = next.scenes.find((s) => s.id === sc.id)!;
  const ids = current.element_ids.filter((eid) => eid !== copy.id);
  const at = ids.indexOf(id);
  ids.splice(at < 0 ? ids.length : at + 1, 0, copy.id);
  next = { ...next, scenes: next.scenes.map((s) => s.id === sc.id ? { ...s, element_ids: ids } : s) };
  return { project: next, id: copy.id };
}

export function elementLabel(el: Element): string {
  if (el.name) return el.name;
  switch (el.type) {
    case "text": return el.text.slice(0, 24);
    case "shape": return el.shape;
    case "image": return "image";
    case "video": return "video";
    case "mesh": return el.mesh;
    case "camera": return "camera";
    case "particles": return el.kind;
    case "html": return "html";
    case "code": return "composition";
  }
}
